import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FileText, Clock, CheckCircle, ArrowRight, PushPin, Buildings, DotsSixVertical, TrayArrowDown, PencilSimple, Gavel } from "@phosphor-icons/react";
import { toast } from "sonner";

function Metric({ label, value, tone = "blue", testid }) {
  const tones = {
    blue: "text-navy",
    amber: "text-amber-600",
    emerald: "text-emerald-600",
    red: "text-red-600",
    zinc: "text-zinc-900",
  };
  return (
    <div className="card-flat p-5" data-testid={testid}>
      <div className="label-caps">{label}</div>
      <div className={`font-display text-3xl font-bold mt-2 ${tones[tone]}`}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    approved: "text-emerald-700 bg-emerald-50 border-emerald-200",
    pending: "text-amber-700 bg-amber-50 border-amber-200",
    rejected: "text-red-700 bg-red-50 border-red-200",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border rounded-sm ${map[status] || "text-zinc-700 bg-zinc-50 border-zinc-200"}`}
    >
      {status}
    </span>
  );
}

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [deps, setDeps] = useState([]);
  const [queue, setQueue] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [pinOrder, setPinOrder] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, r, e, d, q] = await Promise.all([
        api.get("/documents/stats"),
        api.get("/documents"),
        api.get("/documents/expiring", { params: { days: 60 } }),
        api.get("/departments"),
        api.get("/dashboard/my-queue"),
      ]);
      setStats(s.data);
      setRecent(r.data.slice(0, 6));
      setExpiring(e.data.slice(0, 5));
      setDeps(d.data);
      setQueue(q.data?.items || []);
    })();
  }, []);

  // Sync local pin order with auth user
  useEffect(() => {
    setPinOrder(user?.pinned_department_ids || []);
  }, [user]);

  const pinnedDeps = pinOrder
    .map((id) => deps.find((d) => d.id === id))
    .filter(Boolean);

  const persistOrder = async (order) => {
    try {
      const { data } = await api.patch("/users/me/pinned-departments/order", { ordered_ids: order });
      if (setUser) setUser({ ...user, pinned_department_ids: data.pinned_department_ids });
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
      // Roll back on error
      setPinOrder(user?.pinned_department_ids || []);
    }
  };

  const onDragStart = (id) => (e) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    // Firefox requires data to initiate drag
    try { e.dataTransfer.setData("text/plain", id); } catch { /* noop */ }
  };
  const onDragOver = (id) => (e) => {
    e.preventDefault();
    if (dragId && id !== dragId && id !== overId) setOverId(id);
  };
  const onDrop = (targetId) => (e) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) {
      setDragId(null); setOverId(null); return;
    }
    const next = [...pinOrder];
    const from = next.indexOf(dragId);
    const to = next.indexOf(targetId);
    if (from < 0 || to < 0) return;
    next.splice(from, 1);
    next.splice(to, 0, dragId);
    setPinOrder(next);
    setDragId(null); setOverId(null);
    persistOrder(next);
  };
  const onDragEnd = () => { setDragId(null); setOverId(null); };

  return (
    <div className="space-y-8" data-testid="dashboard">
      {pinnedDeps.length > 0 && (
        <div data-testid="pinned-departments">
          <div className="flex items-center justify-between mb-3 px-1">
            <div className="label-caps flex items-center gap-2">
              <PushPin size={14} weight="fill" className="text-cyan" /> Your pinned departments
              {pinnedDeps.length > 1 && (
                <span className="text-[10px] text-slate-400 normal-case tracking-normal ml-1">· drag to reorder</span>
              )}
            </div>
            <Link to="/documents" className="text-[10px] uppercase tracking-widest text-slate-500 hover:text-navy">
              All departments →
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {pinnedDeps.map((d) => (
              <div
                key={d.id}
                draggable={pinnedDeps.length > 1}
                onDragStart={onDragStart(d.id)}
                onDragOver={onDragOver(d.id)}
                onDrop={onDrop(d.id)}
                onDragEnd={onDragEnd}
                data-testid={`pinned-tile-${d.id}`}
                className={`card-flat p-4 transition-all group flex items-center gap-2 relative select-none ${
                  dragId === d.id ? "opacity-40" : ""
                } ${
                  overId === d.id && dragId !== d.id ? "ring-2 ring-cyan" : "hover:border-cyan hover:-translate-y-0.5"
                }`}
              >
                {pinnedDeps.length > 1 && (
                  <DotsSixVertical
                    size={14}
                    className="text-slate-300 group-hover:text-slate-500 cursor-grab active:cursor-grabbing shrink-0"
                    weight="bold"
                  />
                )}
                <Link
                  to={`/departments/${d.id}`}
                  className="flex items-center gap-3 flex-1 min-w-0"
                  onDragStart={(e) => e.preventDefault()} // avoid Link's default drag
                >
                  <div className="w-10 h-10 shrink-0 rounded-sm bg-navy text-cyan flex items-center justify-center font-display text-lg group-hover:bg-cyan group-hover:text-navy transition-colors">
                    {d.name.trim().charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
                      <Buildings size={10} /> Workspace
                    </div>
                    <div className="text-sm font-semibold text-navy leading-tight truncate">{d.name}</div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Metric label="Total documents" value={stats?.total ?? "—"} tone="zinc" testid="stat-total" />
        <Metric label="Pending approval" value={stats?.pending ?? "—"} tone="amber" testid="stat-pending" />
        <Metric label="Approved" value={stats?.approved ?? "—"} tone="emerald" testid="stat-approved" />
        <Metric label="Expiring 30d / Expired" value={`${stats?.expiring_soon ?? "—"} / ${stats?.expired ?? "—"}`} tone="red" testid="stat-expiring" />
      </div>

      {/* Your queue — pending reviews & approvals addressed to CURRENT user */}
      <div className="card-flat" data-testid="your-queue-tile">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-sm bg-navy text-cyan flex items-center justify-center">
              <TrayArrowDown size={18} weight="bold" />
            </div>
            <div>
              <div className="label-caps">Waiting on you</div>
              <div className="font-display text-lg text-navy uppercase mt-0.5">
                Your queue {queue.length > 0 && (
                  <span className="inline-flex items-center justify-center text-[11px] font-mono bg-red-600 text-white px-2 py-0.5 rounded-full ml-1 align-middle" data-testid="queue-count">
                    {queue.length}
                  </span>
                )}
              </div>
            </div>
          </div>
          <Link
            to="/approvals"
            data-testid="link-approvals"
            className="text-xs uppercase tracking-widest text-navy font-semibold hover:text-cyan transition-colors flex items-center gap-1"
          >
            Approvals hub <ArrowRight size={14} />
          </Link>
        </div>

        {queue.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500" data-testid="queue-empty">
            <CheckCircle size={24} className="mx-auto mb-2 text-emerald-500" />
            You&apos;re all caught up — nothing waiting on your action.
          </div>
        ) : (
          <div className="divide-y divide-zinc-200">
            {queue.slice(0, 6).map((it) => {
              const Icon = it.kind === "revision_approval" ? Gavel : PencilSimple;
              const tone =
                it.kind === "edit_approval"
                  ? "text-red-700 bg-red-50 border-red-200"
                  : it.kind === "edit_review"
                  ? "text-blue-700 bg-blue-50 border-blue-200"
                  : "text-amber-700 bg-amber-50 border-amber-200";
              const href = it.kind === "revision_approval"
                ? `/documents/${it.id}`
                : `/documents/${it.id}/edit`;
              return (
                <Link
                  key={`${it.kind}-${it.id}`}
                  to={href}
                  data-testid={`queue-item-${it.id}`}
                  className="flex items-center justify-between p-4 hover:bg-zinc-50 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 shrink-0 rounded-sm bg-zinc-100 text-navy flex items-center justify-center">
                      <Icon size={16} weight="bold" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-zinc-900 truncate">{it.title}</div>
                      <div className="text-xs text-zinc-500 mt-0.5 font-mono flex items-center gap-2">
                        <span className={`inline-flex items-center px-1.5 py-px text-[9px] font-semibold uppercase tracking-wider border rounded-sm ${tone}`}>
                          {it.kind_label}
                        </span>
                        Raised by {it.raised_by}
                        {it.raised_at && <span>· {new Date(it.raised_at).toLocaleDateString()}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs uppercase tracking-widest text-navy font-semibold flex items-center gap-1 ml-3 shrink-0">
                    {it.action_label} <ArrowRight size={14} />
                  </div>
                </Link>
              );
            })}
            {queue.length > 6 && (
              <div className="p-3 text-center text-xs font-mono text-zinc-500">
                + {queue.length - 6} more waiting — see the Approvals hub
              </div>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-flat">
          <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
            <div>
              <div className="label-caps">Recent uploads</div>
              <div className="font-display text-lg text-navy uppercase mt-1">Latest activity</div>
            </div>
            <Link to="/documents" data-testid="link-all-documents" className="text-xs uppercase tracking-widest text-navy font-semibold hover:text-cyan transition-colors flex items-center gap-1">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          <div className="divide-y divide-zinc-200">
            {recent.length === 0 && (
              <div className="p-10 text-center text-sm text-zinc-500">
                <FileText size={28} className="mx-auto mb-2 text-zinc-300" />
                No documents yet. <Link to="/upload" className="text-cyan font-semibold">Upload your first</Link>.
              </div>
            )}
            {recent.map((d) => (
              <Link
                key={d.id}
                to={`/documents/${d.id}`}
                data-testid={`recent-doc-${d.id}`}
                className="flex items-center justify-between p-4 hover:bg-zinc-50 transition-colors"
              >
                <div className="min-w-0">
                  <div className="font-medium text-zinc-900 truncate">{d.title}</div>
                  <div className="text-xs text-zinc-500 mt-0.5 font-mono">v{d.current_version} • {new Date(d.created_at).toLocaleDateString()}</div>
                </div>
                <StatusBadge status={d.status} />
              </Link>
            ))}
          </div>
        </div>

        <div className="card-flat">
          <div className="px-5 py-4 border-b border-zinc-200">
            <div className="label-caps">Expiring soon</div>
            <div className="font-display text-lg text-navy uppercase mt-1">Next 60 days</div>
          </div>
          <div className="divide-y divide-zinc-200">
            {expiring.length === 0 && (
              <div className="p-8 text-center text-sm text-zinc-500">
                <CheckCircle size={24} className="mx-auto mb-2 text-emerald-500" />
                All certificates current.
              </div>
            )}
            {expiring.map((d) => (
              <Link
                key={d.id}
                to={`/documents/${d.id}`}
                data-testid={`expiring-${d.id}`}
                className="block p-4 hover:bg-zinc-50 transition-colors border-l-4 border-orange-500"
              >
                <div className="font-medium text-zinc-900 text-sm">{d.title}</div>
                <div className="text-xs text-orange-600 mt-1 font-mono flex items-center gap-1">
                  <Clock size={12} /> {new Date(d.expiry_date).toLocaleDateString()}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
